import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FileText, Loader2, Printer, Search, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'

function abrirBoletoPDF(contaId: number) {
  const token = localStorage.getItem('pain_token')
  fetch(`/api/financeiro/boletos/${contaId}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob() })
    .then(blob => {
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    })
    .catch(e => toast.error(`Falha no PDF: ${e.message}`))
}

function abrirBoletosLotePDF(ids: number[]) {
  if (!ids.length) return
  const token = localStorage.getItem('pain_token')
  fetch(`/api/financeiro/boletos/pdf-lote?ids=${ids.join(',')}`, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob() })
    .then(blob => {
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    })
    .catch(e => toast.error(`Falha no PDF: ${e.message}`))
}

export default function EmissaoBoletosPage() {
  const qc = useQueryClient()
  const [cargaId, setCargaId] = useState<string>('')
  const [filtroCarga, setFiltroCarga] = useState<string>('')
  const [bancoId, setBancoId] = useState<number | ''>('')
  const [sel, setSel] = useState<Set<number>>(new Set())

  const { data: bancos = [] } = useQuery<any[]>({
    queryKey: ['contas-banco-boleto'],
    queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data,
  })
  const bancosBoleto = bancos.filter((b: any) => b.emite_boleto)

  const { data: pendentes = [], isLoading } = useQuery<any[]>({
    queryKey: ['boletos-pendentes', filtroCarga, bancoId],
    queryFn: async () => (await api.get('/financeiro/boletos/pendentes', {
      params: {
        ...(filtroCarga ? { carga_id: Number(filtroCarga) } : {}),
        ...(bancoId ? { conta_bancaria_id: bancoId } : {}),
      },
    })).data,
  })

  const gerar = useMutation({
    mutationFn: async (contaIds: number[]) => (await api.post('/financeiro/boletos/gerar', {
      conta_ids: contaIds, conta_bancaria_id: bancoId || null,
    })).data,
    onSuccess: (r) => {
      const falhas = (r.resultados || []).filter((x: any) => !x.ok)
      toast.success(`${r.gerados} boleto(s) gerado(s)`)
      if (falhas.length) toast.error(`${falhas.length} falharam: ${falhas[0]?.erro || ''}`)
      setSel(new Set())
      qc.invalidateQueries({ queryKey: ['boletos-pendentes'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao gerar boletos'),
  })

  const toggle = (id: number) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  const pendentesNaoGerados = pendentes.filter((p: any) => !p.ja_gerado)
  const selecionarTodos = () => setSel(new Set(pendentesNaoGerados.map((p: any) => p.conta_id)))
  const idsGerados = new Set<number>(pendentes.filter((p: any) => p.ja_gerado).map((p: any) => p.conta_id))
  const selGerados = Array.from(sel).filter(id => idsGerados.has(id))
  const selNaoGerados = Array.from(sel).filter(id => !idsGerados.has(id))
  const selecionarGerados = () => setSel(new Set(Array.from(idsGerados)))


  const gerarRemessa = async () => {
    if (!bancoId) { toast.error('Escolha o banco emissor'); return }
    const body: any = { conta_bancaria_id: bancoId }
    if (sel.size) body.conta_ids = Array.from(sel)
    else if (filtroCarga) body.carga_id = Number(filtroCarga)
    const token = localStorage.getItem('pain_token')
    const r = await fetch('/api/financeiro/boletos/remessa', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    })
    if (!r.ok) { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Erro na remessa'); return }
    const blob = await r.blob()
    const url = URL.createObjectURL(blob); const a = document.createElement('a')
    a.href = url; a.download = `remessa_${bancoId}_${new Date().toISOString().slice(0, 10)}.REM`; a.click()
    setTimeout(() => URL.revokeObjectURL(url), 60000)
    toast.success('Remessa gerada — suba no internet banking. Os boletos ficam "na remessa" até o retorno confirmar.', { duration: 6000 })
    qc.invalidateQueries({ queryKey: ['boletos-pendentes'] })
  }
  const importarRetorno = async (file: File) => {
    const fd = new FormData(); fd.append('arquivo', file)
    try {
      const d = (await api.post('/financeiro/boletos/retorno', fd)).data
      toast.success(`Retorno: ${d.registrados || 0} registrado(s) · ${d.baixados} baixado(s) de ${d.liquidados} liquidado(s)`, { duration: 6000 })
      if (d.nao_encontrados?.length) toast.error(`${d.nao_encontrados.length} nosso número(s) não encontrado(s)`)
      qc.invalidateQueries({ queryKey: ['boletos-pendentes'] })
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Erro ao importar retorno') }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold flex items-center gap-2"><FileText size={18} /> Emissão de Boletos</h2>
      </div>

      <div className="card card-body bg-amber-50 border-amber-200 text-xs text-amber-800 flex items-start gap-2">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>Fluxo até ficar pagável: <strong>1) Gerar</strong> (simulação — só layout) → <strong>2) Gerar remessa</strong> e subir
          no internet banking do banco → <strong>3) Importar o retorno</strong>: quando o banco confirmar a entrada o boleto
          vira <strong>registrado ✓ (pagável)</strong>. Layout precisa estar <strong>homologado</strong> com o banco antes de cobrar de verdade.</span>
      </div>

      {/* Filtros */}
      <div className="card card-body flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Filtrar por carga (nº)</label>
          <div className="flex gap-2">
            <input className="input w-40" type="number" placeholder="todas" value={cargaId}
              onChange={e => setCargaId(e.target.value)} />
            <button className="btn-secondary" onClick={() => setFiltroCarga(cargaId)}>
              <Search size={14} /> Filtrar
            </button>
            {filtroCarga && <button className="btn-ghost text-xs" onClick={() => { setCargaId(''); setFiltroCarga('') }}>limpar</button>}
          </div>
        </div>
        <div>
          <label className="label">Banco (filtra e emite)</label>
          <select className="input w-56" value={bancoId} onChange={e => { setBancoId(e.target.value ? Number(e.target.value) : ''); setSel(new Set()) }}>
            <option value="">— todos os bancos —</option>
            {bancosBoleto.map((b: any) => <option key={b.id} value={b.id}>{b.nome} ({b.codigo_banco})</option>)}
          </select>
          <p className="text-[11px] text-ink-400 mt-1 w-56">Mostra só os boletos desse banco (o escolhido no faturamento) que ainda não foram pra remessa.</p>
        </div>
        <div className="flex-1" />
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={selecionarTodos} disabled={pendentesNaoGerados.length === 0}>
            Selecionar pendentes ({pendentesNaoGerados.length})
          </button>
          <button className="btn-secondary" onClick={selecionarGerados} disabled={idsGerados.size === 0}>
            Selecionar gerados ({idsGerados.size})
          </button>
          <button className="btn-secondary" disabled={selGerados.length === 0}
            onClick={() => abrirBoletosLotePDF(selGerados)} title="Imprime todos os selecionados (já gerados) num único PDF">
            <Printer size={14} /> Imprimir selecionados {selGerados.length > 0 ? `(${selGerados.length})` : ''}
          </button>
          <button className="btn-primary" disabled={selNaoGerados.length === 0 || gerar.isPending}
            onClick={() => gerar.mutate(selNaoGerados)}>
            {gerar.isPending ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
            Gerar {selNaoGerados.length > 0 ? `(${selNaoGerados.length})` : ''}
          </button>
        </div>
      </div>

      {/* CNAB — registro (remessa) e baixa (retorno), emergência */}
      <div className="card card-body space-y-3">
        <div className="font-semibold text-sm flex items-center gap-2"><FileText size={15} /> CNAB — remessa e retorno (layout conforme o banco: Caixa/Santander/BNB = 400)</div>
        <p className="text-xs text-ink-500">Caminho de arquivo (sem API): <strong>gera a remessa</strong> dos boletos (registro) pra subir no internet banking, e <strong>importa o retorno</strong> do banco pra dar baixa + conciliar automaticamente. Layout a homologar com o banco.</p>
        <div className="flex flex-wrap items-center gap-3">
          <button className="btn-secondary" onClick={gerarRemessa} disabled={!bancoId}>
            <FileText size={14} /> Gerar remessa {sel.size ? `(${sel.size} selec.)` : filtroCarga ? `(carga ${filtroCarga})` : '(todos)'}
          </button>
          <label className="btn-secondary cursor-pointer">
            <Printer size={14} /> Importar retorno
            <input type="file" className="hidden" accept=".ret,.txt,.RET,.crm,*"
              onChange={e => { const f = e.target.files?.[0]; if (f) importarRetorno(f); e.currentTarget.value = '' }} />
          </label>
          <span className="text-[11px] text-ink-400">use o banco emissor selecionado acima</span>
        </div>
      </div>

      {/* Lista */}
      <div className="card">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="w-8"></th>
                <th>Pedido</th><th>Cliente</th><th>Carga</th>
                <th className="text-right">Valor</th><th>Vencimento</th><th>Parc.</th>
                <th>Status</th><th>Linha digitável</th><th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={10} className="text-center py-6 text-ink-400"><Loader2 className="animate-spin inline" size={16} /></td></tr>}
              {!isLoading && pendentes.length === 0 && (
                <tr><td colSpan={10} className="text-center py-6 text-ink-400 text-sm">Nenhum boleto pendente de remessa{bancoId ? ' neste banco' : ''}{filtroCarga ? ' nesta carga' : ''}.</td></tr>
              )}
              {pendentes.map((p: any) => (
                <tr key={p.conta_id} className={
                  sel.has(p.conta_id) ? 'bg-brand-50'
                  : p.registro === 'registrado' ? 'bg-emerald-50'
                  : p.registro === 'remessa' ? 'bg-amber-50'   // já foi pra remessa alguma vez
                  : ''
                }>
                  <td><input type="checkbox" checked={sel.has(p.conta_id)} onChange={() => toggle(p.conta_id)} /></td>
                  <td className="font-medium">#{p.pedido_numero}</td>
                  <td className="text-sm">{p.cliente_nome || '—'}</td>
                  <td className="text-sm text-ink-500">{p.carga_id ? `#${p.carga_id}` : '—'}</td>
                  <td className="text-right font-semibold">{formatMoney(p.valor)}</td>
                  <td className="text-sm">{p.vencimento?.split('-').reverse().join('/')}</td>
                  <td className="text-xs text-ink-500">{p.parcela}/{p.total_parcelas}</td>
                  <td>
                    {p.registro === 'registrado'
                      ? <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium" title={p.registro_em ? `registrado em ${p.registro_em.split('-').reverse().join('/')}` : ''}>registrado ✓</span>
                      : p.registro === 'remessa'
                      ? <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-medium" title={p.remessa_em ? `remessa em ${p.remessa_em.slice(0, 10).split('-').reverse().join('/')}` : ''}>na remessa</span>
                      : p.ja_gerado
                      ? <span className="text-[10px] px-2 py-0.5 rounded bg-sky-100 text-sky-700 font-medium">simulação</span>
                      : <span className="text-[10px] px-2 py-0.5 rounded bg-slate-200 text-slate-600 font-medium">pendente</span>}
                  </td>
                  <td className="font-mono text-[11px] text-ink-600">{p.linha_digitavel || '—'}</td>
                  <td>
                    {p.ja_gerado && (
                      <button onClick={() => abrirBoletoPDF(p.conta_id)}
                        className="flex items-center gap-1 text-brand-600 hover:text-brand-800 text-xs font-medium">
                        <Printer size={13} /> Imprimir
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
